import { ReadonlyVec2, ReadonlyVec4, vec2, vec3 } from 'gl-matrix';
import { SignedDistanceField } from '../../core/models/SignedDistanceField';
import { CustomTargetObject } from '../../data/common/customs/CustomTargetObject';
import { ContourTexture } from '../../data/structure/ContourTexture';

import { ImageObject } from '~/engine/data/image/ImageObject';
import { BoundingBox } from '../../data/common/BoundingBox';
import { Grid } from '~/engine/data/common/Grid';

/**
 * {@link DrawObject} is a {@link CustomTargetObject} used as a drawing
 * buffer for contouring tools. Its grid is always a sub-region of the
 * target image grid so that voxel boundaries (especially slice centers)
 * align exactly with the image. The viewport bounding box only determines
 * which portion of the image grid to cover.
 */
export class DrawObject extends CustomTargetObject {
    /**
     * Planar distance between neighboring pixels along x and y axes (mm/px).
     */
    protected readonly resolution: ReadonlyVec2;

    /**
     * The {@link ReadonlyVec2} describing the size of this {@link DrawObject} in pixels along x and y -axes.
     */
    protected readonly _planarPixelSize: vec2;

    /**
     * Constructs a DrawObject whose grid is a sub-region of the target
     * image grid, covering the visible viewport area. The grid is snapped
     * to image voxel boundaries so that slice centers align exactly.
     *
     * @param target Image whose grid defines voxel alignment.
     * @param resolution Planar pixel spacing in mm/px for the drawing
     *        texture. Controls rendering quality of the brush stroke.
     * @param viewportBoundingBox Visible area in patient space; used only
     *        to determine which image voxels to cover.
     * @param color Outline color for rendering.
     */
    public constructor(
        target: ImageObject,
        resolution: ReadonlyVec2,
        viewportBoundingBox: BoundingBox,
        color: ReadonlyVec4
    ) {
        super(target);
        this.resolution = vec2.clone(resolution);
        this._contourThickness = 0.5;
        this.renderOptions = { fillAlpha: 0.25 };

        if (!viewportBoundingBox.bounds) {
            throw new Error(
                `Cannot create DrawObject: viewport bounding box bounds not defined.`
            );
        }

        // Convert viewport bounds to image voxel space and snap outward
        // to integer voxel boundaries so the DrawObject grid is a true
        // sub-region of the image grid.
        const imageGrid = target.grid;
        const minVoxel = imageGrid.toVoxelSpace(viewportBoundingBox.bounds.min);
        const maxVoxel = imageGrid.toVoxelSpace(viewportBoundingBox.bounds.max);
        const snappedMin = vec3.fromValues(
            Math.floor(Math.min(minVoxel[0], maxVoxel[0])),
            Math.floor(Math.min(minVoxel[1], maxVoxel[1])),
            Math.floor(Math.min(minVoxel[2], maxVoxel[2]))
        );
        const snappedMax = vec3.fromValues(
            Math.ceil(Math.max(minVoxel[0], maxVoxel[0])),
            Math.ceil(Math.max(minVoxel[1], maxVoxel[1])),
            Math.ceil(Math.max(minVoxel[2], maxVoxel[2]))
        );
        const snappedSize = vec3.subtract(
            vec3.create(),
            snappedMax,
            snappedMin
        );

        // Planar pixel count derived from snapped extent and requested
        // resolution. The grid resolution stays at image grid resolution
        // to maintain voxel alignment.
        const extentMm = vec3.fromValues(
            snappedSize[0] * imageGrid.resX,
            snappedSize[1] * imageGrid.resY,
            snappedSize[2] * imageGrid.resZ
        );
        this._planarPixelSize = vec2.fromValues(
            Math.ceil(extentMm[0] / this.resolution[0]),
            Math.ceil(extentMm[1] / this.resolution[1])
        );

        const sizePx = vec3.fromValues(
            this._planarPixelSize[0],
            this._planarPixelSize[1],
            snappedSize[2]
        );
        const cornerWorld = imageGrid.toWorldSpace(snappedMin);

        this._contourTexture = new ContourTexture(
            this._imageGrid,
            sizePx,
            cornerWorld,
            ContourTexture.DEFAULT_MARGIN,
            vec3.fromValues(
                this.resolution[0],
                this.resolution[1],
                imageGrid.resZ
            )
        );

        this._color = [color[0], color[1], color[2]];
    }

    public get grid(): Grid {
        return this._contourTexture!.textureGrid;
    }

    /**
     * Method to get the {@link ReadonlyVec2 size} of this {@link DrawObject} in pixels along x and y -axes.
     */
    public get planarPixelSize(): ReadonlyVec2 {
        return this._planarPixelSize;
    }

    /**
     * Method to get the number of slices of this {@link DrawObject}.\
     */
    public get numberOfSlices(): number {
        return this.grid.sizeZ;
    }

    public toContourTexture(): ContourTexture | undefined {
        const sdf = this._contourTexture!.signedDistanceField;
        const bounds = sdf.getContentBounds();
        if (!bounds) {
            return undefined;
        }

        const { min, max } = bounds;
        const sourceRowLen = max[0] - min[0];
        const sdfBytes = sdf.pixels;

        const marginSlices = 1;
        // add empty slices on top and bottom
        const sdfPixelSize = vec3.fromValues(
            sourceRowLen + 1,
            max[1] - min[1] + 1,
            max[2] - min[2] + 1 + 2 * marginSlices
        );

        // world space coordinates of the corner of the SDF,
        // accounting for the margin slice below
        const cornerWorld = this.grid.toWorldSpace(
            vec3.fromValues(min[0], min[1], min[2] - marginSlices)
        );

        const contourTexture = new ContourTexture(
            this._targetImage.grid,
            sdfPixelSize,
            cornerWorld
        );

        const targetSdf = contourTexture.signedDistanceField;

        // copy the cropped SDF to the new SDF, offsetting by 1 to account for the margins
        const sliceArray = new Uint8Array(sdfPixelSize[0] * sdfPixelSize[1]);
        for (let z = min[2]; z <= max[2]; z++) {
            let targetIdx = 0;
            let sourceIdx = min[0] + min[1] * sdf.size[0];
            for (let y = min[1]; y <= max[1]; y++) {
                sliceArray.set(
                    sdfBytes[z].subarray(sourceIdx, sourceIdx + sourceRowLen),
                    targetIdx
                );
                targetIdx += sdfPixelSize[0];
                sourceIdx += sdf.size[0];
            }
            targetSdf.arrayToTextureSlice(sliceArray, z - min[2] + 1);
        }

        return contourTexture;
    }

    /**
     * Method to get the {@link SignedDistanceField} of this {@link DrawObject}.\
     * This is the {@link SignedDistanceField} on which the {@link DrawObject} is drawn.
     */
    public get signedDistanceField(): Readonly<SignedDistanceField> {
        return this._contourTexture!.signedDistanceField;
    }
}
